/**
 * navigation/components/AppHeader
 * ──────────────────────────────────────────────────────────────────────────
 * The 52px application header. Geometry from the design: `0 18px` padding, a
 * #E4E4E4 bottom rule, `space-between`.
 *
 * LEFT   20px mark · "SK COMPANY CASH" in 12.5px mono 600 uppercase (0.08em) ·
 *        a 1×18px divider · the org line, which ellipsises when space is tight ·
 *        the "SUBLEDGER · NOT MONEY-OF-RECORD" pill, hidden below 1240px.
 *
 * RIGHT  the as-of stamp in 11px mono · the primary "＋ Assign cash".
 *
 * THE PILL IS NOT DECORATION. This product records who holds company money; it
 * is not the money of record, and the bank and the GL are. Saying so in the
 * chrome is what stops someone reconciling against this screen. It is the first
 * thing dropped when the window narrows because it is a standing statement, not
 * a live figure — but it is never dropped on the desktop the console is used on.
 *
 * WHAT IS DELIBERATELY ABSENT: the prototype's Finance/Approver/Custodian
 * selector. The console comes from the signed-in user's permissions, and a
 * switcher in the chrome would imply a person can choose their own authority.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { chrome } from '../../theme/spacing';
import { mono, sans, size as fontSize, type as textRole } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { BP, useResponsive } from '../../lib/responsive';
import { stamp } from '../../lib/dates';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import Logo from '../../components/Logo';

export interface AppHeaderProps {
  /** "Softknack Technologies · org_sk_001", from the platform session. */
  orgLabel: string;
  /** The as-of moment every balance on screen shares. */
  asOf: string | null;
  /** Opens the Assign cash modal. Finance only — omit for other consoles. */
  onAssignCash?: () => void;
  /** Opens the overlay nav. Present only when the sidebar is not docked. */
  onOpenNav?: () => void;
}

export function AppHeader({ orgLabel, asOf, onAssignCash, onOpenNav }: AppHeaderProps) {
  const { width, isMobile } = useResponsive();

  // The design hides the pill below ~1240px, which is exactly the width where
  // the left group starts competing with the right one.
  const showPill = width >= BP.desktop;
  // The as-of stamp is a long mono string; below the tablet edge it would push
  // the primary action off the header.
  const showAsOf = width >= BP.mobile;

  return (
    <View style={styles.header}>
      <View style={styles.left}>
        {!!onOpenNav && (
          <IconButton
            icon="register"
            onPress={onOpenNav}
            accessibilityLabel="Open navigation"
          />
        )}

        <View style={styles.brand}>
          <Logo size={20} />
          {!isMobile && <Text style={textRole.wordmark}>SK Company Cash</Text>}
        </View>

        {!isMobile && <View style={styles.divider} />}

        <Text numberOfLines={1} style={styles.org}>
          {orgLabel}
        </Text>

        {showPill && (
          <View style={styles.pill}>
            <Text style={styles.pillText}>SUBLEDGER · NOT MONEY-OF-RECORD</Text>
          </View>
        )}
      </View>

      <View style={styles.right}>
        {showAsOf && !!asOf && (
          <Text numberOfLines={1} style={styles.asOf}>
            Balances as of {stamp(asOf)}
          </Text>
        )}
        {!!onAssignCash && (
          <Button
            label="Assign cash"
            icon="plus"
            iconStrokeWidth={1.6}
            size="lg"
            variant="primary"
            onPress={onAssignCash}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    height: chrome.headerHeight,
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    paddingHorizontal: chrome.headerPaddingX,
    backgroundColor: neutral.surface,
    borderBottomWidth: 1,
    borderBottomColor: neutral.border,
  },
  // `minWidth: 0` + `flexShrink: 1` is what allows the org label to ellipsise
  // instead of the whole group overflowing.
  left: { flexDirection: 'row', alignItems: 'center', gap: 14, minWidth: 0, flexShrink: 1 },
  brand: { flexDirection: 'row', alignItems: 'center', gap: 9, flexShrink: 0 },
  divider: { height: 18, width: 1, backgroundColor: neutral.border },
  org: {
    ...sans('400'),
    fontSize: fontSize.label,
    color: neutral.grey600,
    minWidth: 60,
    flexShrink: 1,
    ...ellipsisStyle,
  },
  pill: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.pill,
    flexShrink: 0,
  },
  pillText: { ...mono('400'), fontSize: fontSize.tableHeader, color: neutral.grey600 },
  right: { flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 0 },
  asOf: { ...mono('400'), fontSize: fontSize.metaSm, color: neutral.grey600 },
});

export default AppHeader;
